'use client';

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useParams } from 'next/navigation';
import { Button } from '@/components/ui/Button';
import styles from './ClientShowcase.module.css';

interface ClientShowcaseProps {
  translations: {
    badge: string;
    title: string;
    subtitle: string;
    stats: {
      number: string;
      label: string;
    };
    description?: string;
    cta: string;
  };
  locale?: string;
  ctaUrl?: string;
}

const CUSTOMER_LABEL: Record<string, string> = {
  en: 'Jamezz customer',
  nl: 'Jamezz klant',
  de: 'Jamezz Kunde',
  sv: 'Jamezz-kund',
};

const SUPPORTED_LOCALES = ['en', 'nl', 'de', 'sv'];

export default function ClientShowcase({ translations, locale, ctaUrl }: ClientShowcaseProps) {
  const params = useParams();
  const paramLocale = params?.locale as string | undefined;
  // Prefer explicit prop, then URL param (SSR-safe with useParams), then 'en' fallback.
  const detectedLocale = locale
    || (paramLocale && SUPPORTED_LOCALES.includes(paramLocale) ? paramLocale : 'en');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentRef = sectionRef.current;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
          }
        });
      },
      {
        threshold: 0.2, // Trigger when 20% of the section is visible
        rootMargin: '0px',
      }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [isVisible]);

  // Client logos with proper SEO names
  const clientLogos = [
    { src: '/images/customers/the-social-hub.webp', name: 'The Social Hub' },
    { src: '/images/customers/okinii.webp', name: 'Okinii' },
    { src: '/images/customers/westcode.webp', name: 'WestCord Hotels' },
    { src: '/images/customers/movenpick.webp', name: 'Mövenpick Hotels & Resorts' },
    { src: '/images/customers/de-beren.webp', name: 'De Beren' },
    { src: '/images/customers/topgolf.webp', name: 'Topgolf' },
    { src: '/images/customers/eazie.webp', name: 'Eazie' },
    { src: '/images/customers/blijdorp.webp', name: 'Diergaarde Blijdorp' },
    { src: '/images/customers/ado-den-haag.webp', name: 'ADO Den Haag' },
    { src: '/images/customers/vermaat.webp', name: 'Vermaat' },
    { src: '/images/customers/anne-max.webp', name: 'Anne&Max' },
    { src: '/images/customers/la-place.webp', name: 'La Place' },
    { src: '/images/customers/van-der-valk.webp', name: 'Van der Valk' },
    { src: '/images/customers/autogrill.webp', name: 'Autogrill' },
    { src: '/images/customers/winter-wonderland.webp', name: 'Winter Wonderland' },
    { src: '/images/customers/maritim-hotels.webp', name: 'Maritim Hotels' },
    { src: '/images/customers/moeke.webp', name: 'Moeke' },
    { src: '/images/customers/europarcs.webp', name: 'EuroParcs' },
    { src: '/images/customers/olearys.webp', name: "O'Learys" },
    { src: '/images/customers/anytyme.webp', name: 'Anytyme' },
    { src: '/images/customers/bakker-bart.webp', name: 'Bakker Bart' },
    { src: '/images/customers/center-parcs.webp', name: 'Center Parcs' },
    { src: '/images/customers/corendonhotels.webp', name: 'Corendon Hotels' },
    { src: '/images/customers/drippys.webp', name: "Drippy's" },
    { src: '/images/customers/goldies-smashburger.webp', name: 'Goldies Smashburger' },
    { src: '/images/customers/leonardo-hotels.webp', name: 'Leonardo Hotels' },
    { src: '/images/customers/mmaah.webp', name: 'MMAAH' },
    { src: '/images/customers/pureto.webp', name: 'Pureto' },
    { src: '/images/customers/ruffs-burger.webp', name: "Ruff's Burger" },
    { src: '/images/customers/sushidog.webp', name: 'Sushidog' },
    { src: '/images/customers/sushito.webp', name: 'Sushito' },
    { src: '/images/customers/wok-to-go.webp', name: 'Wok to Go' },
  ];

  // Generate positions for balloon effect - spread around the text
  const generateBalloonPosition = (index: number, total: number) => {
    // Divide logos into left and right groups
    const isLeftSide = index % 2 === 0;
    const groupIndex = Math.floor(index / 2);
    
    // Better vertical distribution - create 4 rows on each side
    const rowsPerSide = 4;
    const row = groupIndex % rowsPerSide;
    const column = Math.floor(groupIndex / rowsPerSide);
    
    // Calculate vertical position based on rows with more spacing
    const rowHeight = 160; // More space between rows
    const yOffset = -240 + (row * rowHeight); // 4 rows with better spacing
    
    // Calculate horizontal position with more spacing between columns
    const horizontalOffset = isLeftSide 
      ? -420 - (column * 100)  // Left side: more space between columns
      : 420 + (column * 110);   // Right side: even more space between columns
    
    return {
      '--balloon-x': `${horizontalOffset}px`,
      '--balloon-y': `${yOffset}px`,
      '--balloon-delay': `${(index * 0.05)}s`, // Staggered intro
      '--balloon-size': `110px`,
    } as React.CSSProperties;
  };

  return (
    <div ref={sectionRef} className={styles.showcase}>
      <div className={styles.container}>
        {/* Center Content */}
        <div className={styles.centerContent}>
          <div className={styles.badge}>{translations.badge}</div>
          <h2 className={styles.statsNumber}>{translations.stats.number}</h2>
          <div className={styles.statsLabel}>{translations.stats.label}</div>
          {translations.description && (
            <p className={styles.description}>{translations.description}</p>
          )}
          
          {/* Logos - Between description and button on tablet/mobile */}
          <div className={`${styles.balloonsContainer} ${isVisible ? styles.animate : ''}`}>
            {clientLogos.map((logo, index) => (
              <div 
                key={`balloon-${index}`} 
                className={styles.balloon}
                style={generateBalloonPosition(index, clientLogos.length)}
              >
                <Image
                  src={logo.src}
                  alt={`${logo.name} - ${CUSTOMER_LABEL[detectedLocale as string] || 'Jamezz customer'}`}
                  width={120}
                  height={120}
                  sizes="(max-width: 640px) 60px, (max-width: 1024px) 80px, 110px"
                  className={styles.clientImage}
                  loading="lazy"
                  quality={75}
                />
              </div>
            ))}
          </div>
          
          {/* CTA Button */}
          {ctaUrl && (
            <div className={styles.ctaWrapper}>
                <Button 
                as="a"
                href={ctaUrl}
                  variant="primary" 
                  size="lg" 
                  className={styles.ctaButton}
                  data-cta-id="cta_clients_home"
                  data-cta-position="after_clients"
                  data-cta-variant="primary"
                >
                  {translations.cta}
                </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
